import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useState } from 'react';

import { CartesianGrid, Line, LineChart, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { format } from 'date-fns';
import { patientData } from './PatientData';

const responseColors = [
  '#8F9BB3', // MUI purple for sleep
  '#FECB00', // MUI red for steps
  '#FF994D', // MUI blue for PHQ-9
  '#FF0000', // MUI orange for wakes
];

const PHQReponseLabel: Record<number, string> = {
  0: 'Jamais',
  1: 'Plusieurs jours',
  2: 'Plus de la moitié du temps',
  3: 'Presque tous les jours',
};

const PHQQuestionLabels: Record<number, string> = {
  1: "Peu d'intérêt ou de plaisir à faire les choses",
  2: 'Se sentir triste, déprimé(e) ou sans espoir',
  3: "Avoir des problèmes de sommeil (difficulté à s'endormir, se réveiller trop tôt ou dormir trop)",
  4: "Se sentir fatigué(e) ou manquer d'énergie",
  5: 'Avoir un mauvais appétit ou manger trop',
  6: 'Se sentir mal dans votre estime de soi, ou vous sentir un raté ou avoir des doutes sur votre valeur',
  7: 'Avoir de la difficulté à vous concentrer sur ce que vous faites, par exemple lire ou regarder la télévision',
  8: "Bouger ou parler si lentement que d'autres l'ont remarqué, ou au contraire, être tellement agité(e) que vous vous levez sans cesse",
  9: "Avoir des pensées que vous seriez mieux mort(e) ou de vous faire du mal d'une façon ou d'une autre",
};

interface ResponseChipProps {
  value: number;
  selected?: boolean;
}

const ResponseChip: React.FC<ResponseChipProps> = ({ value, selected }) => {
  return (
    <>
      <Chip label={value} size="medium" sx={{ bgcolor: responseColors[value], color: 'white', mr: 2 }} />
      <Typography display="inline" sx={{ fontWeight: selected ? 'bold' : 'normal', color: selected ? 'primary.main' : 'text.secondary' }}>
        {PHQReponseLabel[value]}
      </Typography>
    </>
  );
};

const formatXAxis = (tickItem: Date) => {
  return format(tickItem, 'dd MMM');
};

const formatLabel = (tickItem: Date) => {
  return format(tickItem, 'dd MMM yyyy');
};

/** 3. Small inline component to display a single question. */
interface QuestionItemProps {
  question: number;
  answer: number;
  onClick: (id: number) => void;
  selected: boolean;
}

const QuestionItem: React.FC<QuestionItemProps> = ({ question, answer, onClick, selected }) => {
  return (
    <Grid
      container
      onClick={() => {
        onClick(question);
      }}
      sx={{
        mb: 2,
        p: 1,
        borderRadius: 6,
        bgcolor: selected ? 'primary.light' : 'background.default',
        cursor: 'pointer',
      }}
    >
      <Grid size={7}>
        <Typography sx={{ mr: 2, color: selected ? 'primary.main' : 'text.secondary', fontWeight: selected ? 'bold' : 'normal' }}>
          {`${question.toString()}. ${PHQQuestionLabels[question]}`}
        </Typography>
      </Grid>
      <Grid size={5}>
        <ResponseChip selected={selected} value={answer} />
      </Grid>
    </Grid>
  );
};

const DashboardQuestionnaire: React.FC = () => {
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);

  const chartData = selectedQuestionId ? patientData.phq9.questions[selectedQuestionId] : patientData.phq9.period.map((d) => ({ day: d.date, value: d.value }));

  const questions = patientData.phq9.period[patientData.phq9.period.length - 1];
  const graphTitle = selectedQuestionId ? PHQQuestionLabels[selectedQuestionId] : 'Dernier PHQ-9';

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      {/* Two-column layout */}
      <Grid container size={6}>
        <Grid size={12}>
          {/* Left column: questions + legend */}
          <Card
            sx={{
              mb: 2,
              borderRadius: 4,
              boxShadow: 0,
              backgroundColor: selectedQuestionId ? 'background.default' : 'primary.light',
              cursor: 'pointer',
            }}
          >
            <CardContent
              onClick={() => {
                setSelectedQuestionId(null);
              }}
            >
              <Grid container>
                <Grid alignItems={'center'} size={8}>
                  <Typography fontWeight="bold" sx={{ color: 'primary.main' }} variant="h5">
                    Score du dernier PHQ-9
                  </Typography>
                </Grid>
                <Grid size={2}>
                  <Typography fontWeight="bold" sx={{ color: 'primary.main' }} variant="h5">
                    {patientData.phq9.period_average}
                  </Typography>
                </Grid>
                <Grid size={2}>
                  <Chip color="primary" label="-3 pts" size="medium" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              boxShadow: 0,
              borderRadius: 4,
              borderColor: 'lightgray',
              border: 1,
              maxHeight: 500,
              overflowY: 'auto',
              p: 2,
            }}
          >
            {questions.values.map((q) => (
              <QuestionItem
                answer={q.value}
                key={q.question}
                onClick={setSelectedQuestionId}
                question={q.question}
                selected={selectedQuestionId == q.question}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <Grid
        container
        size={6}
        sx={{
          bgcolor: 'primary.light',
          borderRadius: 4,
          p: 2,
        }}
      >
        {/* Right column: could show a chart or analytics */}
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 30, bottom: 30 }}>
            <YAxis allowDecimals={false} domain={selectedQuestionId ? [0, 3] : ['auto', 'auto']} tick={{ fontSize: 14 }} />
            <CartesianGrid strokeDasharray="3 0" vertical={false} />
            <Line dataKey="value" stroke="#8884d8" strokeWidth={3} type="monotone" />
            <XAxis angle={-45} dataKey="day" tick={{ textAnchor: 'end', fontSize: 14 }} tickFormatter={formatXAxis} />
            <RechartsTooltip cursor={false} labelFormatter={formatLabel} />
          </LineChart>
        </ResponsiveContainer>
      </Grid>

      {/* Legend for numeric -> frequency mapping */}
      <Grid container size={6} spacing={1} sx={{ p: 1, mt: 2 }}>
        {Object.entries(PHQReponseLabel).map(([n]) => (
          <Grid key={n} size={3}>
            <ResponseChip value={Number(n)} />
          </Grid>
        ))}
      </Grid>
      <Grid alignContent={'center'} size={6}>
        <Typography align="center" sx={{ color: 'primary.main' }} variant="h6">
          {' '}
          {graphTitle}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default DashboardQuestionnaire;
